import { useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { ValueType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { ScheduleType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import { isEmpty } from 'lodash-es';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { ScheduleAvailabilityTypes } from '@frontend/api-schedule-availability';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Button,
  ButtonBar,
  DropdownField,
  FormFieldActionTypes,
  FormRow,
  NakedButton,
  SpinningLoader,
  styles,
  Text,
  TextareaField,
  TextField,
  useAlert,
  useForm,
} from '@frontend/design-system';
import { useAppointmentTypeCheckListDropdown } from '../../components/AppointmentTypeCheckListDropdown';
import { CustomMultiSelectInfiniteDropdownOptionType } from '../../components/CustomMultiSelectInfiniteDropdown';
import { useGetCustomContactSourceId } from '../../hooks/scheduler-v3/use-get-custom-contact-source-id';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { convertScheduleHoursObjectToRRuleObject } from '../../rrule-helper';
import { getProviderWorkingHoursText } from './helpers';
import { ProviderEditableAvatar } from './provider-editable-avatar';

interface Props {
  locationId: string;
  providerId?: string;
  providerDetails?: Provider;
  isLoading?: boolean;
  primaryButtonLabel?: string;
  onClose?: () => void;
  providerWorkingHours?: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek;
  selectedAppointmentTypes?: CustomMultiSelectInfiniteDropdownOptionType[];
  onSelectProviderWorkingHoursClick?: (
    providerDetails: Provider,
    selectedAppointmentTypes: CustomMultiSelectInfiniteDropdownOptionType[]
  ) => void;
}

export const ProviderDetailsForm = ({
  locationId,
  providerId,
  isLoading,
  providerDetails,
  primaryButtonLabel,
  onClose,
  providerWorkingHours,
  onSelectProviderWorkingHoursClick,
  selectedAppointmentTypes = [],
}: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('schedule');
  const isSmall = useMatchMedia({ maxWidth: breakpoints.small.max });
  const { locationTimezone } = useLocationTimezone(locationId);
  const { parentLocationId } = useSchedulingLocationInfo();
  const { customContactSourceId } = useGetCustomContactSourceId();
  const { isScheduleDefinitionsEnabled } = SchedulerV3.Helpers.useGetScheduleDefinitionsFeatureFlagDetails();

  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();

  const isCreate = !providerId;
  const requireFullName = isIntegratedOffice ? isCreate : true;

  const isDisplayNameChangedManually = useRef(Boolean(providerDetails?.displayName));
  const [providerPhoto, setProviderPhoto] = useState(providerDetails?.profile?.picUrl || '');
  const { formProps, getFieldProps, values, seedValues, isComplete } = useForm({
    fields: {
      firstName: { type: 'text', required: requireFullName, value: providerDetails?.firstName },
      lastName: { type: 'text', required: requireFullName, value: providerDetails?.lastName },
      displayName: { type: 'text', required: true, value: providerDetails?.displayName },
      bio: { type: 'text', required: false, value: providerDetails?.profile?.bio },
      providerWorkingHours: { type: 'dropdown', required: false, value: '' },
    },
    fieldStateReducer: (state, action) => {
      if (
        action.type === FormFieldActionTypes.Update &&
        ['firstName', 'lastName'].includes(action.payload.name) &&
        !isDisplayNameChangedManually.current
      ) {
        // Update displayName when firstName or lastName changes
        const displayName = getFullName({ firstName: state.firstName.value, lastName: state.lastName.value });
        return {
          ...state,
          displayName: {
            ...state.displayName,
            value: displayName,
            error: displayName ? '' : t('Display Name is required'),
          },
        };
      }
      if (action.type === FormFieldActionTypes.Update && action.payload.name === 'displayName') {
        // Set flag to true when displayName is changed manually
        isDisplayNameChangedManually.current = true;
      }
      return state;
    },
  });
  const providerWorkingHoursText = useMemo(
    () => getProviderWorkingHoursText(providerWorkingHours ?? {}),
    [providerWorkingHours]
  );

  const updateProviderMutation = SchedulerV3.Mutations.useUpdateProviderMutation();
  const createProviderMutation = SchedulerV3.Mutations.useCreateProviderMutation();
  const createScheduleMutation = SchedulerV3.Mutations.useCreateScheduleMutation({
    options: {
      onError: () => {
        alert.error(t('Failed to create Provider schedule'));
      },
    },
  });
  const { createAndDeleteMappingsAsync, isLoading: isUpdatingMappings } =
    SchedulerV3.Mutations.useCreateAndDeleteMappings();

  const {
    AppointmentTypeCheckListDropdownElement,
    getAppointmentTypeSelectedUnselectedValues,
    hasAppointmentTypeCheckListDropdownError,
  } = useAppointmentTypeCheckListDropdown({
    locationId: locationId,
    providerId: providerId ?? '',
    selectedOptions: selectedAppointmentTypes,
  });
  const { isSingleTypeScope } = useAppScopeStore();
  const isMultiLocation = !isSingleTypeScope;

  const { hasWSM, isLoading: isLoadingIntegrationDetails } = SchedulerV3.Helpers.useCheckHasWSM(locationId);

  // Seed values on practitioner details change
  useEffect(() => {
    seedValues({
      firstName: providerDetails?.firstName || '',
      lastName: providerDetails?.lastName || '',
      displayName: providerDetails?.displayName || '',
      bio: providerDetails?.profile?.bio ?? '',
    });
    setProviderPhoto(providerDetails?.profile?.picUrl ?? '');
    isDisplayNameChangedManually.current = Boolean(providerDetails?.displayName);
  }, [providerDetails, seedValues]);

  const getPayloadForUpdateProvider = (childLocationId?: string): SchedulerV3.Types.UpdateProviderIO['input'] => ({
    id: providerId || '',
    firstName: values.firstName || '',
    lastName: values.lastName || '',
    displayName: values.displayName || '',
    hidden: providerDetails?.hidden,
    profile: {
      ...providerDetails?.profile,
      bio: values?.bio,
      picUrl: providerPhoto,
    },
    // Explicitly use the childLocationId if provided, otherwise keep the usage of providerDetails.locationId. Although the providerDetails.locationId is the parentLocationId for a multi-location.
    childLocationId: isMultiLocation ? childLocationId || providerDetails?.locationId : undefined,
  });

  const getPayloadForCreateProvider = (): SchedulerV3.Types.CreateProviderIO['input'] => ({
    locationId: locationId,
    sourceTenantId: locationId || '',
    firstName: values.firstName || '',
    lastName: values.lastName || '',
    displayName: values.displayName || '',
    hidden: false,
    profile: {
      act: true,
      bio: values?.bio,
      picUrl: providerPhoto,
    },
    // for non-integrated location, we need to set the sourceId as customContactSourceId,
    // for integrated location, BE set the sourceId as the integration's SourceID automatically and on FE we were not allowing create provider for integrated location
    sourceId: customContactSourceId,
    parentLocationId: isMultiLocation ? parentLocationId : undefined,
  });
  const handleUpdateProvider = async () => {
    if (!providerId) {
      return;
    }

    const changedAppointmentTypes = getAppointmentTypeSelectedUnselectedValues();
    const payload = getPayloadForUpdateProvider(locationId);

    const updateProvider = () => updateProviderMutation.mutateAsync(payload);
    const updateMappings = () =>
      createAndDeleteMappingsAsync({
        locationId: locationId,
        providerId: providerId,
        values: changedAppointmentTypes.newSelectedAppointmentTypeIds,
        valueType: ValueType.VT_APPOINTMENT_TYPE,
        mappingIdsForDelete: changedAppointmentTypes.mappingIdsForDelete,
      });

    return Promise.all([updateProvider(), updateMappings()])
      .then(() => {
        alert.success(t('Provider details updated successfully'));
        onClose?.();
      })
      .catch(() => {
        alert.error(t('Failed to update Provider'));
      });
  };
  const handleCreateProvider = async () => {
    const changedAppointmentTypes = getAppointmentTypeSelectedUnselectedValues();
    const payload = getPayloadForCreateProvider();

    let isProviderCreated = false;
    let isMappingsCreated = false;
    try {
      // create provider
      const res = await createProviderMutation.mutateAsync(payload);

      isProviderCreated = true;

      // create its mapping
      await createAndDeleteMappingsAsync({
        locationId: locationId,
        providerId: res.provider.id,
        values: changedAppointmentTypes.newSelectedAppointmentTypeIds,
        valueType: ValueType.VT_APPOINTMENT_TYPE,
      });
      isMappingsCreated = true;

      if (!isScheduleDefinitionsEnabled && !isEmpty(providerWorkingHours)) {
        const { workingHours, breaks } = convertScheduleHoursObjectToRRuleObject(
          providerWorkingHours,
          locationTimezone
        );

        await createScheduleMutation.mutateAsync({
          id: res.provider.id,
          locationId,
          type: ScheduleType.PROVIDER,
          recurrenceRules: {
            workingHours: workingHours,
            breaks: breaks,
          },
        });
      }
      alert.success(t('Provider created successfully'));
      onClose?.();
    } catch (error) {
      console.error('Error creating provider or mappings:', error);
      if (isProviderCreated && !isMappingsCreated) {
        alert.error(t('Failed to create Provider Appointment Type mappings'));
        onClose?.();
      } else if (!isProviderCreated) {
        alert.error(t('Failed to create Provider'));
      } else {
        alert.error(t('Failed to create Provider working hours'));
        onClose?.();
      }
    }
  };
  const handleSave = () => {
    providerId ? handleUpdateProvider() : handleCreateProvider();
  };
  const handleSelectProviderWorkingHours = () => {
    // Note: this function is called only for new providers
    // and setting this values in state back to maintain it while navigation between provider-details and provider-working-hours
    const updatedProvider = {
      firstName: values.firstName,
      lastName: values.lastName,
      displayName: values.displayName,
      profile: {
        bio: values.bio,
        picUrl: providerPhoto,
      },
    } as Provider;

    onSelectProviderWorkingHoursClick?.(updatedProvider, getAppointmentTypeSelectedUnselectedValues().selectedItems);
  };

  if (isLoading || isLoadingIntegrationDetails) {
    return (
      <div css={styles.flexCenter}>
        <SpinningLoader size='small' />
      </div>
    );
  }

  const isSaving = createProviderMutation.isLoading || updateProviderMutation.isLoading || isUpdatingMappings;

  return (
    <>
      <form {...formProps} css={{ overflowY: 'auto' }}>
        <ProviderEditableAvatar
          locationId={locationId}
          providerName={getFullName(values) || getFullName(providerDetails ?? {})}
          providerPhoto={providerPhoto}
          onChangeProviderPhoto={setProviderPhoto}
        />

        <FormRow cols={[100, 100]} css={practitionerTextFieldContainerStyles(isSmall)}>
          <TextField
            {...getFieldProps('firstName')}
            label={t('First Name')}
            disabled={!hasWSM}
            helperText={!hasWSM ? t("The provider's first name as it appears in your Practice Management System.") : ''}
          />
          <TextField
            {...getFieldProps('lastName')}
            label={t('Last Name')}
            disabled={!hasWSM}
            helperText={!hasWSM ? t("The provider's last name as it appears in your Practice Management System.") : ''}
          />
        </FormRow>
        <FormRow cols={[100]} css={practitionerTextFieldContainerStyles(isSmall)}>
          <TextField
            {...getFieldProps('displayName')}
            label={t('Display Name')}
            helperText={t('This name will be visible to customers when booking online, and on your Weave calendar')}
          />
        </FormRow>
        {!isScheduleDefinitionsEnabled && AppointmentTypeCheckListDropdownElement}
        <FormRow cols={[100]} css={textAreaFieldContainerStyles}>
          <TextareaField label={t('Provider Biography')} {...getFieldProps('bio')} />
        </FormRow>
        <Text size='small' color='subdued'>
          {t(
            "Enter a brief description of the provider's background & expertise. Patients will see this when booking appointments online."
          )}
        </Text>
        {!providerId && !isScheduleDefinitionsEnabled && (
          <NakedButton
            onClick={handleSelectProviderWorkingHours}
            css={{ marginTop: theme.spacing(2), '> *': { textAlign: 'left' } }}
          >
            <DropdownField
              {...getFieldProps('providerWorkingHours')}
              tabIndex={-1}
              css={{ maxWidth: 500, pointerEvents: 'none' }}
              label={t('Provider Working Hours')}
              helperText={t('Office hours will be used if working hours are not set.')}
              icon={() => <Icon name='caret-right-small' color='light' />}
              value={providerWorkingHoursText}
            >
              <DropdownField.Option value=''>{t('Select Working Hours')}</DropdownField.Option>
              <DropdownField.Option value={providerWorkingHoursText}>{providerWorkingHoursText}</DropdownField.Option>
            </DropdownField>
          </NakedButton>
        )}
      </form>
      <ButtonBar css={{ padding: theme.spacing(1, 0, 0, 0) }}>
        <Button
          disabled={!isComplete || hasAppointmentTypeCheckListDropdownError}
          loading={isSaving}
          onClick={handleSave}
          size='large'
          type='button'
        >
          {primaryButtonLabel || t('Save')}
        </Button>
      </ButtonBar>
    </>
  );
};

const practitionerTextFieldContainerStyles = (isSmall: boolean) =>
  css({
    display: 'flex',
    flexDirection: isSmall ? 'column' : 'row',
    rowGap: theme.spacing(3),
    ...(isSmall ? { marginRight: 0 } : {}),
  });

const textAreaFieldContainerStyles = css({
  marginBottom: theme.spacing(1),
  paddingTop: theme.spacing(1),
  flexDirection: 'column',
});
